import { useCallback, useEffect, useRef, useState } from 'react'
import { type SupabaseClient, type RealtimeChannel } from '@supabase/supabase-js'
import { createUUID } from '@hydrologiq/common/helpers/uuid'

// ─── Row types matching the database schema ────────────────────────────────────

export interface SystemNodeRow {
  id: string
  system: string
  tenant: string
  asset: string | null
  position_x: number
  position_y: number
  created_at: string
  updated_at: string
}

export interface SystemConnectionRow {
  id: string
  source_node: string
  target_node: string
  source_handle: string | null
  target_handle: string | null
  tenant: string
  created_at: string
}

// ─── Hook options ──────────────────────────────────────────────────────────────

export interface UseSystemRealtimeOptions {
  systemId: string
  tenantId: string
  supabase: SupabaseClient | null
  onNodeInsert: (node: SystemNodeRow) => void
  onNodeUpdate: (node: Partial<SystemNodeRow> & { id: string }) => void
  onNodeDelete: (nodeId: string) => void
  onConnectionInsert: (connection: SystemConnectionRow) => void
  onConnectionDelete: (connectionId: string) => void
}

// ─── Connection status type ────────────────────────────────────────────────────

export type RealtimeConnectionStatus = 'connected' | 'disconnected' | 'reconnecting'

// ─── Deduplication TTL (ms) ────────────────────────────────────────────────────

const RECENT_WRITE_TTL_MS = 2000

// ─── Token refresh interval (ms) ──────────────────────────────────────────────
// Supabase JWTs from our backend are short-lived. Refresh well before expiry
// so the Realtime WebSocket always has a valid token.
const TOKEN_REFRESH_INTERVAL_MS = 50 * 60 * 1000 // 50 minutes

// ─── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Subscribes to Supabase Realtime postgres_changes on system_node and
 * system_connection tables. Calls the provided callbacks on each event type.
 *
 * Returns the current connection status of the channel and an `addRecentWrite`
 * function that external code (drag handlers, connection handlers) can call to
 * suppress echo events from the current client for ~2 seconds.
 */
export function useSystemRealtime(options: UseSystemRealtimeOptions): {
  status: RealtimeConnectionStatus
  addRecentWrite: (id: string) => void
} {
  const { systemId, tenantId, supabase } = options

  const [status, setStatus] = useState<RealtimeConnectionStatus>('disconnected')

  // Store callbacks in refs so the subscription doesn't re-create when
  // callbacks change (avoids unnecessary re-subscriptions).
  const callbacksRef = useRef(options)
  callbacksRef.current = options

  // ─── Deduplication: recent writes map (id → timestamp) ─────────────────────
  const recentWritesRef = useRef<Map<string, number>>(new Map())

  /**
   * Add an ID to the recent writes set. Events for this ID arriving via
   * realtime within the TTL window will be skipped (the local state already
   * reflects the change from the optimistic update).
   */
  const addRecentWrite = useCallback((id: string) => {
    recentWritesRef.current.set(id, Date.now())
  }, [])

  /**
   * Returns true if the given ID is in the recent writes set (not yet expired).
   * Also prunes expired entries on each check.
   */
  const isRecentWrite = useCallback((id: string): boolean => {
    const now = Date.now()
    const map = recentWritesRef.current

    // Prune expired entries
    for (const [key, timestamp] of map) {
      if (now - timestamp > RECENT_WRITE_TTL_MS) {
        map.delete(key)
      }
    }

    return map.has(id)
  }, [])

  useEffect(() => {
    if (!systemId || !tenantId || !supabase) {
      setStatus('disconnected')
      return
    }

    // Use a unique channel name per effect invocation to prevent reusing an
    // already-subscribed channel instance (which would throw when adding
    // postgres_changes callbacks after subscribe).
    const channelName = `system-realtime:${systemId}:${createUUID()}`

    const channel: RealtimeChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_node',
          filter: `system=eq.${systemId}`,
        },
        (payload) => {
          const node = payload.new as SystemNodeRow
          if (isRecentWrite(node.id)) return
          callbacksRef.current.onNodeInsert(node)
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'system_node',
          filter: `system=eq.${systemId}`,
        },
        (payload) => {
          const updated = payload.new as Partial<SystemNodeRow> & { id: string }
          if (isRecentWrite(updated.id)) return
          callbacksRef.current.onNodeUpdate(updated)
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'system_node',
          filter: `system=eq.${systemId}`,
        },
        (payload) => {
          const nodeId = (payload.old as { id: string }).id
          if (isRecentWrite(nodeId)) return
          callbacksRef.current.onNodeDelete(nodeId)
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_connection',
          filter: `tenant=eq.${tenantId}`,
        },
        (payload) => {
          const connection = payload.new as SystemConnectionRow
          if (isRecentWrite(connection.id)) return
          callbacksRef.current.onConnectionInsert(connection)
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'system_connection',
          filter: `tenant=eq.${tenantId}`,
        },
        (payload) => {
          const connectionId = (payload.old as { id: string }).id
          if (isRecentWrite(connectionId)) return
          callbacksRef.current.onConnectionDelete(connectionId)
        }
      )

    channel.subscribe((channelStatus) => {
      switch (channelStatus) {
        case 'SUBSCRIBED':
          setStatus('connected')
          break
        case 'CHANNEL_ERROR':
        case 'TIMED_OUT':
          setStatus('reconnecting')
          break
        case 'CLOSED':
          setStatus('disconnected')
          break
        default:
          // Covers initial 'SUBSCRIBING' state — keep current status
          break
      }
    })

    return () => {
      supabase.removeChannel(channel)
      setStatus('disconnected')
    }
  }, [systemId, tenantId, supabase, isRecentWrite])

  // ─── Periodic token refresh for the Realtime WebSocket ─────────────────────
  // The accessToken callback is invoked on initial connection, but the
  // long-lived WebSocket needs its token refreshed before expiry. Calling
  // setAuth() without arguments triggers the accessToken callback and pushes
  // the fresh token to all active channels.
  useEffect(() => {
    if (!supabase) return

    const interval = setInterval(() => {
      supabase.realtime.setAuth()
    }, TOKEN_REFRESH_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [supabase])

  return { status, addRecentWrite }
}
